import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const API_BASE = "http://127.0.0.1:8000";

function App() {
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [jdText, setJdText] = useState("");
  const [question, setQuestion] = useState("");

  const [uploadStatus, setUploadStatus] = useState("");
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  const [instructions, setInstructions] = useState(
    localStorage.getItem("aiInstructions") ||
      "Respond in a confident, simple, interview-friendly tone."
  );

  const [finalAnswer, setFinalAnswer] = useState("Your answer will appear here…");
  const [loading, setLoading] = useState(false);

  // 🔹 AUTO SCALE STATE
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      const baseWidth = 1200;
      const currentWidth = window.innerWidth;
      const newScale = currentWidth < baseWidth ? currentWidth / baseWidth : 1;
      setScale(newScale);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ================= Resume Upload =================
  const handleResumeUpload = async () => {
    if (!resumeFile) {
      setUploadStatus("⚠️ Select a resume first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", resumeFile);

    try {
      setUploadStatus("⏳ Reading resume...");
      const res = await axios.post(
        `${API_BASE}/utils/extract_text`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setResumeText(res.data.text || "");
      setUploadStatus("✅ Resume extracted!");
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Resume extraction failed.");
    }
  };

  // ================= Save Instructions =================
  const handleSaveInstructions = () => {
    localStorage.setItem("aiInstructions", instructions);
    alert("Instructions saved!");
  };

  // ================= Voice Recording (Option B) =================
  const startRecording = () => {
    if (!window.webkitSpeechRecognition) {
      alert("Your browser does not support voice input. Use Chrome.");
      return;
    }

    // Start a NEW conversation: clear previous transcript
    setQuestion("");

    const recog = new window.webkitSpeechRecognition();
    recog.lang = "en-US";
    recog.continuous = true;       // keep listening
    recog.interimResults = true;   // live updates

    recog.onstart = () => {
      setRecording(true);
    };

    recog.onresult = (event) => {
      // Build FULL transcript from all results (append style)
      let fullText = "";
      for (let i = 0; i < event.results.length; i++) {
        fullText += event.results[i][0].transcript + " ";
      }
      setQuestion(fullText.trim());
    };

    recog.onend = () => {
      // If we didn't manually stop, restart (for continuous listening)
      if (recording) {
        recog.start();
      }
    };

    recog.onerror = (e) => {
      console.error("Speech recognition error:", e.error);
    };

    recog.start();
    recognitionRef.current = recog;
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setRecording(false);
  };

  // ================= Generate Answer (long & detailed) =================
  const handleGenerateAnswer = async () => {
    if (!resumeText) return alert("Upload resume first.");
    if (!jdText) return alert("Paste JD first.");
    if (!question) return alert("Ask or record a question first.");

    setLoading(true);
    setFinalAnswer("⏳ Generating detailed answer…");

    try {
      const baseInstructions =
        instructions ||
        "Respond in a confident, simple, interview-friendly tone.";

      // Force long, detailed, structured answers for interviews
      const finalInstructions =
        baseInstructions +
        " Give a longer, detailed, well-structured answer suitable for a spoken interview. Do not be brief; explain with clarity, examples, and context.";

      const payload = {
        resume_text: resumeText,
        jd_text: jdText,
        question,
        instructions: finalInstructions,
      };

      const res = await axios.post(
        `${API_BASE}/ai/stream_resume`,
        payload,
        { responseType: "text" } // fastest mode
      );

      setFinalAnswer(res.data);
    } catch (err) {
      console.error(err);
      setFinalAnswer("❌ Failed to get response from backend.");
    }

    setLoading(false);
  };

  // ================= UI RENDER =================
  return (
    <div style={styles.page}>

      {/* TOP AUTO-SCALING INTERVIEW CONTROLS */}
      <div style={styles.topWrapper}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}>
          <div style={styles.topRow}>

            {/* LEFT BUTTONS */}
            <div style={styles.leftControls}>
              {!recording ? (
                <button onClick={startRecording} style={styles.buttonBlue}>
                  🎙️ Record
                </button>
              ) : (
                <button onClick={stopRecording} style={styles.buttonRed}>
                  ⏹️ Stop
                </button>
              )}

              <button
                onClick={handleGenerateAnswer}
                style={styles.buttonGreen}
                disabled={loading}
              >
                {loading ? "⚡ Generating..." : "⚡ Generate"}
              </button>
            </div>

            {/* CENTER ANSWER BOX */}
            <div style={styles.answerBox}>
              <h3 style={{ textAlign: "center" }}>💬 AI Answer</h3>
              <div style={styles.answerScroll}>
                <pre style={styles.answerText}>{finalAnswer}</pre>
              </div>
            </div>

            {/* RIGHT QUESTION BOX */}
            <div style={styles.questionBox}>
              <h3>❓ Question (Live Transcript)</h3>
              <textarea
                style={styles.questionArea}
                placeholder="Speak or type the interviewer's question..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
            </div>

          </div>
        </div>
      </div>

      {/* BOTTOM CONTENT – Normal Mode */}
      <div style={styles.section}>
        <h3>🧭 AI Instructions</h3>
        <textarea
          style={styles.textarea}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <button onClick={handleSaveInstructions} style={styles.buttonPurple}>
          Save Instructions
        </button>
      </div>

      <div style={styles.section}>
        <h3>📄 Upload Resume</h3>
        <input
          type="file"
          accept=".pdf,.txt,.docx"
          onChange={(e) => setResumeFile(e.target.files[0])}
        />
        <button onClick={handleResumeUpload} style={styles.buttonOrange}>
          Upload Resume
        </button>
        <p>{uploadStatus}</p>
      </div>

      <div style={styles.section}>
        <h3>💼 Job Description</h3>
        <textarea
          style={styles.textarea}
          placeholder="Paste JD here…"
          value={jdText}
          onChange={(e) => setJdText(e.target.value)}
        />
      </div>

    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#0c0c0e",
    color: "#e5e5e5",
    padding: 20,
    fontFamily: "Arial, sans-serif",
    boxSizing: "border-box",
  },

  topWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 30,
  },

  topRow: {
    display: "grid",
    gridTemplateColumns: "180px 420px 340px",
    gap: "22px",
    alignItems: "flex-start",
  },

  leftControls: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  answerBox: {
    width: "420px",
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(12px)",
    borderRadius: "18px",
    padding: "16px",
    border: "1px solid rgba(255,255,255,0.15)",
    boxShadow: "0 0 18px rgba(0,0,0,0.35)",
  },

  answerScroll: {
    maxHeight: "260px",
    overflowY: "auto",
    padding: "12px",
    background: "rgba(255,255,255,0.03)",
    borderRadius: "14px",
  },

  answerText: {
    margin: 0,
    whiteSpace: "pre-wrap",
    fontSize: "14px",
    color: "#dcdcdc",
  },

  questionBox: {
    width: "340px",
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(12px)",
    borderRadius: "18px",
    padding: "16px",
    border: "1px solid rgba(255,255,255,0.15)",
    boxShadow: "0 0 18px rgba(0,0,0,0.35)",
  },

  questionArea: {
    width: "100%",
    minHeight: "200px",
    padding: "12px",
    borderRadius: "14px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
    fontSize: "14px",
  },

  section: {
    marginTop: 35,
  },

  textarea: {
    width: "100%",
    minHeight: "130px",
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(10px)",
    borderRadius: "18px",
    border: "1px solid rgba(255,255,255,0.15)",
    padding: "14px",
    color: "#fff",
    fontSize: "14px",
  },

  buttonBlue: {
    padding: "10px 15px",
    backgroundColor: "rgba(59,130,246,0.3)",
    border: "1px solid #3B82F6",
    borderRadius: "12px",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 0 12px #3B82F6",
  },

  buttonRed: {
    padding: "10px 15px",
    backgroundColor: "rgba(239,68,68,0.3)",
    border: "1px solid #EF4444",
    borderRadius: "12px",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 0 12px #EF4444",
  },

  buttonGreen: {
    padding: "10px 15px",
    backgroundColor: "rgba(22,163,74,0.3)",
    border: "1px solid #16A34A",
    borderRadius: "12px",
    color: "#fff",
    cursor: "pointer",
    boxShadow: "0 0 12px #16A34A",
  },

  buttonPurple: {
    padding: "10px 15px",
    backgroundColor: "rgba(139,92,246,0.3)",
    border: "1px solid #8B5CF6",
    borderRadius: "12px",
    color: "#fff",
    marginTop: 10,
    cursor: "pointer",
    boxShadow: "0 0 12px #8B5CF6",
  },

  buttonOrange: {
    padding: "10px 15px",
    backgroundColor: "rgba(249,115,22,0.3)",
    border: "1px solid #F97316",
    borderRadius: "12px",
    color: "#fff",
    marginTop: 10,
    cursor: "pointer",
    boxShadow: "0 0 12px #F97316",
  },
};

export default App;
